import React, { createContext, useContext, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, CheckCircle2, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AppContext";
import { getRefCode } from "@/lib/referral";

const LeadCtx = createContext(null);
export const useLead = () => useContext(LeadCtx);

const PRODUCT_OPTIONS = [
  "Spirulina + Sea Buckthorn — 30 Day Box (Combo)",
  "Spirulina",
  "Sea Buckthorn",
  "Noni",
  "Flax Oil",
  "Glucosamine",
  "General Enquiry",
];

export function LeadProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [preselect, setPreselect] = useState("");
  const openLead = useCallback((product) => { setPreselect(product || ""); setOpen(true); }, []);
  return (
    <LeadCtx.Provider value={{ openLead }}>
      {children}
      <LeadDialog open={open} onOpenChange={setOpen} preselect={preselect} />
    </LeadCtx.Provider>
  );
}

function LeadDialog({ open, onOpenChange, preselect }) {
  const { user } = useAuth();
  const [f, setF] = useState({ full_name: "", mobile: "", email: "", city: "", product_interest: preselect || "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  React.useEffect(() => {
    if (open) {
      setF((p) => ({
        ...p,
        full_name: p.full_name || user?.name || "",
        email: p.email || user?.email || "",
        product_interest: preselect || p.product_interest,
      }));
      setDone(false);
      setSubmitted(null);
    }
  }, [open, preselect, user]);

  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(f.mobile.replace(/\D/g, "").slice(-10))) { toast.error("Please enter a valid 10-digit mobile number"); return; }
    setBusy(true);
    try {
      const ref_code = getRefCode();
      const payload = { ...f, mobile: f.mobile.replace(/\D/g, "").slice(-10), ref_code: ref_code || undefined };
      await api.post("/leads", payload);
      setSubmitted(payload);
      setDone(true);
      setF({ full_name: "", mobile: "", email: "", city: "", product_interest: preselect || "", notes: "" });
    } catch { toast.error("Could not send. Please try again."); }
    finally { setBusy(false); }
  };

  const openWhatsAppConfirm = () => {
    const msg = `Hi ASURE, I just submitted an enquiry on asurelife.com — please confirm and let me know the next steps.\n\nName: ${submitted?.full_name || ""}\nProduct: ${submitted?.product_interest || ""}\nCity: ${submitted?.city || ""}`;
    window.open(`https://wa.me/918269784515?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-white border-[color:var(--asure-border)]">
        <div className="bg-asure-forest text-white p-6 relative">
          <button onClick={() => onOpenChange(false)} className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid="lead-close"><X size={18} /></button>
          <div className="eyebrow" style={{ color: "var(--asure-gold)" }}>ASURE Enquiry</div>
          <DialogTitle className="font-display text-3xl mt-2">I'm Interested</DialogTitle>
          <DialogDescription className="text-white/80 text-sm mt-2">Share your details and our team will reach out with pricing, availability and delivery information.</DialogDescription>
        </div>
        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 size={56} className="mx-auto text-asure-forest" />
            <div className="font-display text-3xl mt-4">Thank you!</div>
            <p className="text-asure-text2 mt-2 text-sm max-w-sm mx-auto">
              We've received your enquiry. Our team will call you within <span className="font-semibold text-asure-forest">24 hours</span> at{submitted?.mobile ? ` +91 ${submitted.mobile}` : ""}.
            </p>
            <div className="mt-6 rounded-2xl border border-[color:var(--asure-border)] p-4 bg-asure-ivory text-left">
              <div className="flex items-center gap-2 text-xs font-bold text-asure-forest">
                <MessageCircle size={14} /> Get instant confirmation on WhatsApp
              </div>
              <p className="text-[12px] text-asure-text2 mt-1 leading-relaxed">
                Tap below to open WhatsApp — a pre-filled message will start the chat with our team and you'll get a reply within minutes.
              </p>
              <button onClick={openWhatsAppConfirm} data-testid="lead-whatsapp-confirm"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:brightness-95">
                <MessageCircle size={14} /> Confirm on WhatsApp
              </button>
            </div>
            <button onClick={() => onOpenChange(false)} className="mt-5 text-sm text-asure-text2 hover:text-asure-forest link-underline" data-testid="lead-close-thanks">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3" data-testid="lead-form">
            <Field label="Full Name" v={f.full_name} on={on("full_name")} tid="lead-name" />
            <Field label="Mobile Number" v={f.mobile} on={on("mobile")} tid="lead-mobile" type="tel" placeholder="10-digit number" />
            <Field label="Email (optional)" v={f.email} on={on("email")} tid="lead-email" type="email" req={false} />
            <Field label="City" v={f.city} on={on("city")} tid="lead-city" />
            <div>
              <label className="eyebrow block mb-2">Product Interest</label>
              <select required value={f.product_interest} onChange={on("product_interest")} data-testid="lead-product"
                className="w-full border border-[color:var(--asure-border)] rounded-lg px-4 py-3 bg-white text-asure-text focus:border-asure-forest outline-none">
                <option value="" disabled>Select a product</option>
                {PRODUCT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <button className="btn-primary w-full" disabled={busy} data-testid="lead-submit">{busy ? "Sending…" : "Send Enquiry"}</button>
            <p className="text-[11px] text-asure-text2 text-center pt-2">By submitting, you agree to be contacted by ASURE regarding your enquiry.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, on, type = "text", req = true, tid, placeholder }) {
  return (
    <div>
      <label className="eyebrow block mb-2">{label}</label>
      <input required={req} type={type} value={v} onChange={on} data-testid={tid} placeholder={placeholder}
        className="w-full border border-[color:var(--asure-border)] rounded-lg px-4 py-3 bg-white focus:border-asure-forest outline-none" />
    </div>
  );
}
